import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { retryFeedbackEmail, sendFeedbackReply } from './api';
import type { FeedbackDetail } from './types';

const deliveryLabel = (state: string) => ({ sent: 'E-Mail versandt', pending: 'E-Mail wartet auf Versand',
  sending: 'E-Mail wird versandt', failed: 'E-Mail fehlgeschlagen', uncertain: 'E-Mail-Zustellung unklar',
  unavailable: 'Keine E-Mail-Adresse vorhanden' })[state] ?? 'E-Mail-Zustellung prüfen';

export function FeedbackReplyPanel({ feedback, app, canWrite }: { feedback: FeedbackDetail; app: string; canWrite: boolean }) {
  const client = useQueryClient();
  const [body, setBody] = useState('');
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [attempted, setAttempted] = useState(false);
  const refresh = () => client.invalidateQueries({ queryKey: ['feedback'] });
  const send = useMutation({ mutationFn: () => sendFeedbackReply(feedback.id, app, body.trim(), requestId),
    onSuccess: () => { setBody(''); setAttempted(false); setRequestId(crypto.randomUUID()); void refresh(); } });
  const retry = useMutation({ mutationFn: (id: string) => retryFeedbackEmail(feedback.id, app, id), onSuccess: () => { void refresh(); } });
  const recipient = feedback.user?.email ?? feedback.email;
  const canSend = Boolean(feedback.userId || recipient);
  return <section className="flex flex-col gap-3" aria-label="Antworten">
    <h3 className="text-sm font-semibold">Antworten</h3>
    <p className="text-xs text-text-secondary">{feedback.userId
      ? `Die Antwort wird in der App gespeichert${recipient ? ` und per E-Mail an ${recipient} verschickt` : ''}.`
      : recipient ? `Nur per E-Mail an ${recipient}: Dieses Feedback ist keinem App-Konto zugeordnet.`
        : 'Kein App-Konto und keine Rückadresse vorhanden. Eine Antwort ist nicht möglich.'}</p>
    {(feedback.replies ?? []).map(message => <article key={message.id} className="rounded-md bg-bg-base p-3 text-sm">
      <p className="whitespace-pre-wrap break-words">{message.body}</p>
      <p className="mt-2 text-xs text-text-secondary">{new Date(message.createdAt).toLocaleString('de-DE')} · {deliveryLabel(message.emailState)}
        {feedback.userId && ` · ${message.readAt ? 'In der App gelesen' : 'In der App ungelesen'}`}</p>
      {message.canRetryEmail && canWrite && <button type="button" className="mt-2 min-h-12 text-sm text-accent-light"
        disabled={retry.isPending || send.isPending} onClick={() => retry.mutate(message.id)}>E-Mail erneut zustellen</button>}
      {['uncertain', 'sending'].includes(message.emailState) && !message.canRetryEmail && <p className="mt-2 text-xs text-text-secondary">
        Versandstatus neu laden. Bei länger unklarer Zustellung bitte den Mailversand prüfen, bevor du erneut antwortest.
      </p>}
    </article>)}
    {canWrite && canSend && <>
      <label className="text-sm" htmlFor="feedback-reply">Deine Antwort</label>
      <textarea id="feedback-reply" aria-label="Deine Antwort" className="rounded-md border border-border-subtle bg-bg-base p-3 text-sm"
        rows={6} maxLength={8000} value={body} readOnly={send.isPending || attempted}
        onChange={event => setBody(event.target.value)} />
      <span className="text-xs text-text-secondary">{body.length}/8000</span>
      <button type="button" className="min-h-12 rounded-md bg-accent px-4 py-2 text-white disabled:opacity-50"
        disabled={!body.trim() || send.isPending || retry.isPending}
        onClick={() => { setAttempted(true); send.mutate(); }}>
        {send.isPending ? 'Antwort wird gespeichert …' : attempted ? 'Übermittlung erneut prüfen' : 'Antwort senden'}
      </button>
    </>}
    {(send.isError || retry.isError) && <p role="alert" className="text-sm text-danger">
      {send.error instanceof Error ? send.error.message : retry.error instanceof Error ? retry.error.message : 'Die Übermittlung konnte nicht bestätigt werden.'}
      {' '}Dein Text bleibt erhalten. Prüfe die Übermittlung erneut, bevor du eine weitere Antwort schreibst.
    </p>}
    <button type="button" className="min-h-12 text-sm text-accent-light" onClick={() => { void refresh(); }}>Versandstatus neu laden</button>
  </section>;
}
